import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Package, Calculator, ArrowRight } from "lucide-react";
import axios from "axios";

function Home() {
  const navigate = useNavigate();
  const [kycCompleted, setKycCompleted] = useState(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [session, token] = document.cookie.split("=");
        if (!token) return;
        const userResponse = await axios.get(
          `${REACT_APP_BACKEND_URL}/order/getUser`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setKycCompleted(userResponse.data.kycDone);
      } catch (err) {
        setKycCompleted(false);
      }
    };
    fetchUserData();
  }, [REACT_APP_BACKEND_URL]);

  return (
    <div className="w-full min-h-screen p-4 md:p-6 space-y-6">
      {/* KYC Section: Only show if not completed */}
      {!kycCompleted && (
        <div className="w-full relative bg-gradient-to-r from-[#e6fcf5] via-white to-[#e6fcf5] rounded-2xl shadow-md border border-[#0CBB7D]/40 flex flex-col sm:flex-row items-start sm:items-center gap-3 px-4 py-3 mb-6">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="bg-[#0CBB7D]/10 rounded-2xl p-2 flex items-center justify-center flex-shrink-0">
              <span className="text-[#0CBB7D] text-lg sm:text-xl">💡</span>
            </div>
            <div className="flex flex-col truncate">
              <p className="text-sm font-semibold text-gray-800 truncate">
                Complete your KYC to start shipping
              </p>
              <p className="hidden sm:block text-xs text-gray-600 mt-1 truncate">
                Unlock full features: ship orders, track deliveries, manage
                billing and access support.
              </p>
            </div>
          </div>
          <Link
            to="/Kyc"
            className="w-full sm:w-auto mt-2 sm:mt-0 rounded-xl bg-[#0CBB7D] text-white text-xs md:text-sm font-semibold px-5 py-2 shadow hover:bg-[#079662] transition-colors flex-shrink-0 text-center"
          >
            Complete KYC
          </Link>
        </div>
      )}

      {/* Create Order Card */}
      <div className="relative w-full bg-white rounded-2xl shadow-md hover:shadow-lg transition duration-300 overflow-hidden">
        {/* Background Accents */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0CBB7D]/5 via-transparent to-[#0CBB7D]/5 pointer-events-none"></div>
        <div className="absolute right-0 top-0 w-44 h-44 md:w-60 md:h-60 bg-[#0CBB7D]/10 rotate-12 translate-x-12 -translate-y-10 rounded-3xl"></div>
        <div className="absolute left-12 bottom-0 w-32 h-32 md:w-44 md:h-44 bg-gradient-to-tr from-[#0CBB7D]/10 to-transparent rounded-full blur-2xl"></div>
        <div className="absolute -left-10 top-1/2 w-24 h-24 bg-[#0CBB7D]/5 rounded-full blur-xl"></div>
        <div className="absolute right-6 bottom-6 w-20 h-20 bg-[#0CBB7D]/5 rounded-full blur-lg"></div>

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6 p-5 md:p-8">
          {/* Left section */}
          <div className="flex items-start gap-3 md:gap-5">
            <div className="bg-[#0CBB7D]/10 p-3 md:p-4 rounded-2xl flex-shrink-0">
              <Package className="text-[#0CBB7D] w-7 h-7 md:w-8 md:h-8" />
            </div>
            <div>
              <h2 className="text-base md:text-md font-[500] text-gray-700">
                Create Order
              </h2>
              <p className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 max-w-md">
                Generate and manage shipments seamlessly with pickup scheduling
                and real-time tracking.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-0 md:gap-y-1 mt-2 md:mt-3 text-xs md:text-xs text-gray-400">
                <span>✔ Single & Bulk Orders</span>
                <span>✔ Pickup Scheduling</span>
                <span>✔ Real-time Tracking</span>
                <span>✔ COD Support</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/dashboard/order/neworder")}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 md:px-5 md:py-2.5 bg-[#0CBB7D] text-white rounded-xl hover:bg-[#0a9d69] transition-colors duration-500 ease-in-out font-medium text-sm"
          >
            Create Order <ArrowRight size={16} />
          </button>
        </div>
        <div className="h-1 w-full bg-[#0CBB7D]"></div>
      </div>

      {/* Rate Calculator Card */}
      <div className="relative w-full bg-white rounded-2xl shadow-md hover:shadow-lg transition duration-300 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0CBB7D]/5 via-transparent to-[#0CBB7D]/5 pointer-events-none"></div>
        <div className="absolute left-0 bottom-0 w-44 h-44 md:w-60 md:h-60 bg-[#0CBB7D]/10 -rotate-12 -translate-x-12 translate-y-10 rounded-3xl"></div>
        <div className="absolute right-12 top-0 w-32 h-32 md:w-44 md:h-44 bg-gradient-to-bl from-[#0CBB7D]/10 to-transparent rounded-full blur-2xl"></div>
        <div className="absolute -right-10 top-1/2 w-24 h-24 bg-[#0CBB7D]/5 rounded-full blur-xl"></div>
        <div className="absolute left-6 top-6 w-20 h-20 bg-[#0CBB7D]/5 rounded-full blur-lg"></div>

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6 p-5 md:p-8">
          <div className="flex items-start gap-3 md:gap-5">
            <div className="bg-[#0CBB7D]/10 p-3 md:p-4 rounded-2xl flex-shrink-0">
              <Calculator className="text-[#0CBB7D] w-7 h-7 md:w-8 md:h-8" />
            </div>
            <div>
              <h2 className="text-base md:text-md font-[500] text-gray-700">
                Calculate Rate
              </h2>
              <p className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 max-w-md">
                Estimate shipping costs instantly across couriers with smart
                volumetric calculations.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-0 md:gap-y-1 mt-2 md:mt-3 text-xs md:text-xs text-gray-400">
                <span>✔ Multi-Courier Support</span>
                <span>✔ Compare Prices</span>
                <span>✔ Volumetric Weight</span>
                <span>✔ Cost Optimization</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/dashboard/tools/Cost_Estimation")}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 md:px-5 md:py-2.5 bg-[#0CBB7D] text-white rounded-xl hover:bg-[#0a9d69] transition-colors duration-500 ease-in-out font-medium text-sm"
          >
            Calculate Now <ArrowRight size={16} />
          </button>
        </div>
        <div className="h-1 w-full bg-[#0CBB7D]"></div>
      </div>
    </div>
  );
}

export default Home;
